import type { LevelData } from '../world/level'
import { compileLevel } from '../world/level'

/**
 * The 15 authored campaign floors: 3 acts × (4 floors + 1 boss gate).
 * Authored as ASCII (tiles + entity glyphs) compiled into the SAME frozen
 * LevelData schema the procedural generator emits.
 *
 * DECISION: authored levels live as ASCII-in-TS rather than raw JSON files —
 * the compile step emits the frozen schema, so downstream code sees identical
 * JSON structure; ASCII keeps 15 maps reviewable and editable by hand.
 * `scripts/validate-levels.ts` compiles every map (spawns, exits, teleporter
 * pairing, unknown chars) as a build gate; short rows pad with wall.
 *
 * Glyphs: @ spawn · g/r/w/m generators (husk/sentry/static/foreman)
 *         h/n/o/u enemies · f food · k key · p magic · t treasure
 *         a archive fragment · B boss
 *
 * Archive fragments (8 authored, acts 1-3): f01 side room, f03 behind the
 * destructible pocket, f04 the teleporter landing, f07 (the ARCHIVE itself)
 * behind its destructible, f08 in the relay/pad vault, f09 the corner stash,
 * f12 inside the shuttered exit vault, f13 behind the locked treasure room.
 * Tiles:  # wall · . floor · D door · L locked · E exit · X destructible
 *         T trap · 1-4 teleporter pairs · R relay · G shutter
 *
 * Puzzle arc (operator-directed): f03 introduces the relay circuit, f04
 * introduces teleporters, f08 composes relays with its teleporter network,
 * f12 is the full puzzle-box (teleporter vault, dig-for-relay, gated exit).
 */

// ——— ACT 1 — INDUCTION ———————————————————————————————————————————————

const f01 = compileLevel(
  { id: 'a1f1', name: 'INTAKE', floor: 1, act: 1, band: 0, exit: 'reach' },
  [
    '############################',
    '#.........#######..........#',
    '#..@......#.....#....f.....#',
    '#.........D.....D..........#',
    '#......#..#..g..#..........#',
    '#......#..#.....#...####D###',
    '#......#..###D###...#......#',
    '#..f...#............#..t...#',
    '#......#............#..a...#',
    '#......##########...#......#',
    '#...............#...##D#####',
    '#.......h.......#..........#',
    '#...............D........E.#',
    '############################'
  ]
)

const f02 = compileLevel(
  { id: 'a1f2', name: 'PROCESSING', floor: 2, act: 1, band: 0, exit: 'generators', genOverrides: [{ budget: 14 }, { budget: 14 }] },
  [
    '################################',
    '#..@.......#........#......t...#',
    '#..........#...g....#..........#',
    '#..........D........D..........#',
    '#..........#........#......h...#',
    '#####D######........#####D######',
    '#..........#TTTTTTTT#..........#',
    '#....f.....#........#....g.....#',
    '#..........D........D..........#',
    '#..........#........#..........#',
    '#..h.......#........#.......h..#',
    '#..........#...ff...#..........#',
    '######D#####........#####D######',
    '#..........#........#..........#',
    '#...t......D........D.......E..#',
    '#..........#........#..........#',
    '################################'
  ]
)

// relay-circuit introduction: two relays in plain sight, shutter on the exit —
// the floor teaches "latch them all, the shutters retract" before f08 hides them
const f03 = compileLevel(
  { id: 'a1f3', name: 'SWITCHYARD', floor: 3, act: 1, band: 1, exit: 'reach' },
  [
    '####################################',
    '#..@......#.............#....a..t.#',
    '#.........#.....r.......X.....t...#',
    '#.........D.............#.........#',
    '#.........#.............###########',
    '#####D#####.....h..h....#.........#',
    '#.........#.............#....f....#',
    '#...g.....#.............D.........#',
    '#.........######D########.........#',
    '#.........#.............#.....R...#',
    '#..f......#.....n.......#.........#',
    '#.........#.............###########',
    '######D####.....XX......#.........#',
    '#.........#.....tt......G.........#',
    '#..R.h....#.....XX......#....E.t..#',
    '#.........#.............#.........#',
    '####################################'
  ]
)

const f04 = compileLevel(
  { id: 'a1f4', name: 'SUBFLOOR', floor: 4, act: 1, band: 1, exit: 'reach', auditorDelays: [80] },
  [
    '####################################',
    '#..@.....#........#........#....1.#',
    '#........#...w....#....f...#..a...#',
    '#........D........D........#......#',
    '#........#........#........###D####',
    '####D#####........##D#######......#',
    '#........#........#........#..p...#',
    '#..f.....#..o..o..#....g...#......#',
    '#........#........#........#......#',
    '#........##########........##D#####',
    '#...1....#........#........#......#',
    '#........#....t...D........#...t..#',
    '####D#####...tt...#........#......#',
    '#........#....t...##########......#',
    '#...h....D........#........#..E...#',
    '#........#....o...D........#......#',
    '####################################'
  ]
)

const f05 = compileLevel(
  { id: 'a1f5', name: 'WARDEN GATE', floor: 5, act: 1, band: 1, isBossGate: true, exit: 'boss', bossId: 'warden' },
  [
    '##############################',
    '#............................#',
    '#..@.........................#',
    '#.........#........#.........#',
    '#.........#........#.........#',
    '#.............B..............#',
    '#.........#........#.........#',
    '#.........#........#.........#',
    '#..f..................f......#',
    '#............................#',
    '#.........#........#.........#',
    '#....p....#........#.........#',
    '#.........#...E....#.........#',
    '#.........#........#.........#',
    '##############################'
  ]
)

// ——— ACT 2 — PRODUCTION ——————————————————————————————————————————————

const f06 = compileLevel(
  { id: 'a2f1', name: 'FABRICATION', floor: 6, act: 2, band: 2, exit: 'generators', genOverrides: [{ budget: 16 }, { budget: 16 }, { budget: 12 }] },
  [
    '####################################',
    '#..@.....#.........TT..#..........#',
    '#........#....g....TT..D....r.....#',
    '#........D.........TT..#..........#',
    '#........#.............#..........#',
    '####D#####.............#.....t....#',
    '#........#.............############',
    '#..f.....#....h..h.....#..........#',
    '#........#.............D.....f....#',
    '#........####..####D####..........#',
    '#........#.............####D#######',
    '#..n.....#.....g........#.........#',
    '#........#..............#....E....#',
    '#........#......f.......#.........#',
    '####################################'
  ]
)

const f07 = compileLevel(
  { id: 'a2f2', name: 'ARCHIVE', floor: 7, act: 2, band: 2, exit: 'generators' },
  [
    '####################################',
    '#...........#..........#....a.....#',
    '#..@........#..g...g...#....t.t...#',
    '#...........D..........X..........#',
    '#...........#....u.....#....t.t...#',
    '#...........############..........#',
    '#####D#####.............###########',
    '#.........#.............#.........#',
    '#....f....#.....h.h.....D....f....#',
    '#.........#......h......#.........#',
    '#.........D.............#....p....#',
    '#.........#.............###########',
    '#..o......#....TTTT.....#.........#',
    '#.........#.............L....E....#',
    '#...f.....#.....k.......#.........#',
    '####################################'
  ]
)

// the teleporter floor, upgraded into a routing puzzle: three relays sit at
// the far ends of the pad network, and the exit room is shuttered — riding
// the pads correctly IS the route
const f08 = compileLevel(
  { id: 'a2f3', name: 'TRANSIT', floor: 8, act: 2, band: 3, exit: 'reach', auditorDelays: [60] },
  [
    '####################################',
    '#..@....#.....2#.......#.....1....#',
    '#.......#......#...w...#..........#',
    '#.......#......#.......#....R.....#',
    '#..1....#......D.......D......f...#',
    '#.......#......#.......#..........#',
    '####D####......#..o.o..####D#######',
    '#.......#......#.......#..........#',
    '#..f....#..n...#.......#.....n....#',
    '#.......#......#########..........#',
    '#.......#......#.R.....#......3...#',
    '#..o....#......D...f...#..........#',
    '####D####......#.......############',
    '#....a..#..3...#...p...#..........#',
    '#..R....#......#.......G....E.....#',
    '#..2....#......#.......#..........#',
    '####################################'
  ]
)

const f09 = compileLevel(
  { id: 'a2f4', name: 'QUOTA', floor: 9, act: 2, band: 3, exit: 'generators', genOverrides: [{ budget: 18 }, { budget: 14 }, { budget: 14 }, { budget: 10 }] },
  [
    '####################################',
    '#..@.......#...........#..........#',
    '#..........#....g......#.....g....#',
    '#..........D........u..D..........#',
    '#..........#...........#..........#',
    '#####D######...........####D#######',
    '#..........#...........#..........#',
    '#....f.....#....r......#....f.....#',
    '#..........#...........#..........#',
    '#..........#####D#######..........#',
    '#..h..h....#...........#..........#',
    '#..........#.....k.....#....n.....#',
    '######D#####...........##D#########',
    '#..........#.....w.....#..........#',
    '#...t.t....L...........#.....E....#',
    '#....a.....#...p.......#..........#',
    '####################################'
  ]
)

const f10 = compileLevel(
  // trap-ring arena: an octagonal band of floor hazards circles the Overseer,
  // so its slam shockwaves shove the dodge across live plates; corner pillars
  // are the only clean cover from charges
  { id: 'a2f5', name: 'OVERSEER GATE', floor: 10, act: 2, band: 3, isBossGate: true, exit: 'boss', bossId: 'overseer' },
  [
    '##################################',
    '#................................#',
    '#..@.............................#',
    '#....##.....TTT..TTT.....##......#',
    '#....##...TT........TT...##......#',
    '#........T............T..........#',
    '#........T.....B......T..........#',
    '#........T............T..........#',
    '#..f......TT........TT........f..#',
    '#....##.....TTT..TTT.....##......#',
    '#....##..................##......#',
    '#....p...........................#',
    '#..............E.................#',
    '#................................#',
    '##################################'
  ]
)

// ——— ACT 3 — SANCTION ————————————————————————————————————————————————

const f11 = compileLevel(
  { id: 'a3f1', name: 'SANCTION', floor: 11, act: 3, band: 4, exit: 'generators' },
  [
    '####################################',
    '#..@...#.......#........#.........#',
    '#......#...g...D....r...#....f....#',
    '#......D.......#........D.........#',
    '#......#.......#........#.........#',
    '###D####..TT...#..TT....#####D#####',
    '#......#.......#........#.........#',
    '#..f...#...u...#....o...#....g....#',
    '#......#.......#........#.........#',
    '#......###D#########D####.........#',
    '#..n...#................#.....t...#',
    '#......#....h..h..h.....##D########',
    '###D####................#.........#',
    '#......#....TT....p.....#....E....#',
    '#..t...#................#.........#',
    '####################################'
  ]
)

// the act-3 puzzle-box: one relay buried behind destructible walls (dig for
// it), one inside a sealed vault reachable only by teleporter, one guarded by
// the assembler — all while the audit schedule runs. Both entrances to the
// exit room are shuttered.
const f12 = compileLevel(
  { id: 'a3f2', name: 'BLACKSITE', floor: 12, act: 3, band: 4, exit: 'reach', auditorDelays: [50, 110] },
  [
    '####################################',
    '#..@....#..........X...........t..#',
    '#.......#....w.....X...............',
    '#.......X..........X...XXX........#',
    '#.......#..........X...XRX........#',
    '####X####..o...o...X...XXX........#',
    '#.......#..........X..............#',
    '#..f.1..#..........X....#####G#####',
    '#.......#####X######....#.........#',
    '#.......#..........#....#..f.E..a.#',
    '#..o....#....g.....D....G.........#',
    '#.......#..R.......#....#....p....#',
    '#.......#..........#....###########',
    '#..t....#....h.....#.....#R1t#....#',
    '#.......#..........#.....#####....#',
    '####################################'
  ]
)

const f13 = compileLevel(
  // the 'm' foreman-assembler is the act-3 escalation: small budget, slow pulse,
  // but every foreman it fields boosts the other lines (overrides are row-major)
  { id: 'a3f3', name: 'PAYROLL', floor: 13, act: 3, band: 5, exit: 'generators', genOverrides: [{ budget: 16 }, { budget: 16 }, { budget: 12 }, { budget: 5, interval: 9 }, { budget: 12 }] },
  [
    '####################################',
    '#..@.......#............#..a..t...#',
    '#..........#....g...g...L..t...t..#',
    '#..........D............#.....t...#',
    '#..........#....u.......###########',
    '#####D######............#.........#',
    '#..........#............D....f....#',
    '#....f.....#....r.......#.........#',
    '#..........#......m.....#....k....#',
    '#..........######D#######.........#',
    '#..h..h....#............###########',
    '#..........#.....w......#.........#',
    '######D#####............#....E....#',
    '#..........#....k..p....#.........#',
    '#...t.t....L............D.........#',
    '#..........#............#.........#',
    '####################################'
  ]
)

const f14 = compileLevel(
  { id: 'a3f4', name: 'SIGNAL FLOOR', floor: 14, act: 3, band: 5, exit: 'reach', auditorDelays: [45] },
  [
    '####################################',
    '#..@....#......#.......#......2...#',
    '#.......#..w...#...r...#..........#',
    '#.......#......D.......D..........#',
    '#..1....#......#.......#.....f....#',
    '#.......#......#.......#..........#',
    '####D####..o...#..u....###D########',
    '#.......#......#.......#..........#',
    '#..f....#......#..TT...#....n.....#',
    '#.......########..TT...#..........#',
    '#.......#......#.......#.....1....#',
    '#..n....#..p...D.......###D########',
    '####D####......#.......#..........#',
    '#.......#......#...w...#..........#',
    '#..2....#......#.......D.....E....#',
    '#.......#......#.......#..........#',
    '####################################'
  ]
)

const f15 = compileLevel(
  // destructible-pillar arena: every shelter is temporary — the Director's
  // set erodes as stray fire chews the crumbling clusters away
  { id: 'a3f5', name: 'DIRECTOR GATE', floor: 15, act: 3, band: 5, isBossGate: true, exit: 'boss', bossId: 'director' },
  [
    '######################################',
    '#....................................#',
    '#..@.................................#',
    '#..........XX............XX.........#',
    '#..........XX............XX.........#',
    '#....................................#',
    '#.....XX..........B...........XX.....#',
    '#.....XX......................XX.....#',
    '#..f..............................f..#',
    '#....................................#',
    '#..........XX............XX.........#',
    '#..........XX............XX.........#',
    '#....p...........................p...#',
    '#.................E..................#',
    '#....................................#',
    '######################################'
  ]
)

export const AUTHORED_LEVELS: LevelData[] = [f01, f02, f03, f04, f05, f06, f07, f08, f09, f10, f11, f12, f13, f14, f15]
